import React, { useContext, useState, useEffect, useCallback, useRef } from 'react'
import AppLayout from '../layouts/AppLayout'
import { AppContext } from '../contexts/AppContext'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { HiArrowNarrowRight } from 'react-icons/hi'
import '../styles/matchday.css'
import CompetitionSelector from '../components/CompetitionSelector'

const EditMatchdayData = () => {
  const { competitionId, matchDay } = useParams()
  const { getmatchdaysdata, getmatchdaydata, getmatchdayclosurerates, updatematchdaydata, isLoading, getCompetitions } =
    useContext(AppContext)

  const [matchdayData, setMatchdayData] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [slctdCompetition, setslctdCompetition] = useState(undefined)
  const [matchdays, setMatchdays] = useState([])
  const [matchdaysCompetitionId, setMatchdaysCompetitionId] = useState()
  const matchdaysRequest = useRef(0)
  const [matchday, setMatchday] = useState()
  const [twitterNotified, setTwitterNotified] = useState(0)
  const [twitterSuspended, setTwitterSuspended] = useState(0)
  const [tiktokNotified, setTiktokNotified] = useState(0)
  const [tiktokSuspended, setTiktokSuspended] = useState(0)
  const [telegramNotified, setTelegramNotified] = useState(0)
  const [telegramSuspended, setTelegramSuspended] = useState(0)
  const [telegramNonSuspended, setTelegramNonSuspended] = useState(0)
  const [discordSuspended, setDiscordSuspended] = useState(0)
  const [snapchatNotified, setSnapchatNotified] = useState(0)
  const [telegramImpacted, setTelegramImpacted] = useState(0)
  const [closureData, setclosureData] = useState([])
  const [closureRates, setClosureRates] = useState({})
  const [highlightRates, setHighlightRates] = useState({})
  const [closureTimes, setClosureTimes] = useState({})
  const [highlightTimes, setHighlightTimes] = useState({})

  const [competitions, setCompetitions] = useState([])

  const fetchCompetitions = useCallback(async () => {
    const response = await getCompetitions()
    setCompetitions(response)
  }, [getCompetitions])

  useEffect(() => {
    fetchCompetitions()
  }, [])

  const fetchMatchDaysData = useCallback(
    async (competitionid) => {
      const requestId = ++matchdaysRequest.current
      const response = await getmatchdaysdata(competitionid)
      // Only the latest request may fill the table, so the rows always belong to
      // the competition their Edit links point at.
      if (requestId !== matchdaysRequest.current) return
      setMatchdays(response || [])
      setMatchdaysCompetitionId(competitionid)
    },
    [getmatchdaysdata],
  )

  useEffect(() => {
    if (slctdCompetition) {
      fetchMatchDaysData(slctdCompetition.id)
    }
  }, [slctdCompetition, fetchMatchDaysData])

  // Keep the selector on the competition being edited (e.g. after opening an
  // edit link directly), instead of letting it fall back to the default one.
  useEffect(() => {
    if (!competitionId || !competitions?.length) return
    const competition = competitions.find((comp) => String(comp.id) === String(competitionId))
    if (competition) setslctdCompetition(competition)
  }, [competitionId, competitions])

  useEffect(() => {
    // The page stays mounted when moving between matchdays, so hide the form
    // until this matchday's values are loaded: nothing typed meanwhile gets
    // overwritten and the previous matchday's values are never shown or saved.
    setIsOpen(false)
    setError('')
    if (!matchDay || !competitionId) return

    let cancelled = false

    const fetchdata = async () => {
      const [response, response2] = await Promise.all([
        getmatchdaydata(matchDay, competitionId),
        getmatchdayclosurerates(matchDay, competitionId),
      ])
      // A failed load would show zeros that overwrite the saved data, so keep the form closed.
      if (cancelled || !response || !response2) return

      const data = response[0] || {}
      setMatchdayData(response)
      setTwitterNotified(data.accnotifiedtwitter ?? 0)
      setTwitterSuspended(data.accsuspendedtwitter ?? 0)
      setTiktokNotified(data.accnotifiedtiktok ?? 0)
      setTiktokSuspended(data.accsuspendedtiktok ?? 0)
      setTelegramNotified(data.accnotifiedtelegram ?? 0)
      setTelegramSuspended(data.accsuspendedtelegram ?? 0)
      setTelegramNonSuspended(data.accnonsuspendedtelegram ?? 0)
      setDiscordSuspended(data.accsuspendeddiscord ?? 0)
      setSnapchatNotified(data.accnotifiedsnapchat ?? 0)
      setTelegramImpacted(data.membersinspactedtelegram ?? 0)

      // Missing values become '' (never null) so every input stays controlled
      // and shows exactly what will be sent.
      const byPlatform = (field) =>
        response2.reduce((acc, curr) => {
          acc[curr.id] = curr[field] ?? ''
          return acc
        }, {})

      setclosureData(response2)
      setClosureRates(byPlatform('rate'))
      setHighlightRates(byPlatform('highlightrate'))
      setClosureTimes(byPlatform('time'))
      setHighlightTimes(byPlatform('highlighttime'))
      setIsOpen(true)
    }

    fetchdata()

    return () => {
      cancelled = true
    }
  }, [matchDay, competitionId, getmatchdaydata, getmatchdayclosurerates])

  const navigate = useNavigate()

  const sortMatchdays = (matchdays) => {
    if (!matchdays || matchdays.length === 0) return matchdays

    const getSortOrder = (matchDay) => {
      const matchDayStr = String(matchDay).toUpperCase().trim()

      if (/^\d+$/.test(matchDayStr)) {
        const num = parseInt(matchDayStr)
        return -num
      }

      if (
        matchDayStr.includes('ROUND OF 16') ||
        matchDayStr.includes('ROUND 16') ||
        matchDayStr.includes('ROUND16') ||
        matchDayStr.includes('ROUNDOF16')
      ) {
        return 10000
      }

      if (
        matchDayStr.includes('ROUND OF 8') ||
        matchDayStr.includes('ROUND 8') ||
        matchDayStr.includes('ROUND8') ||
        matchDayStr.includes('ROUNDOF8')
      ) {
        return 20000
      }

      if (matchDayStr.includes('SEMI FINAL') || matchDayStr.includes('SEMIFINAL')) {
        return 30000
      }

      if (matchDayStr.includes('3RD') || matchDayStr.includes('THIRD')) {
        return 40000
      }

      if (matchDayStr.includes('FINAL') && !matchDayStr.includes('SEMI')) {
        return 50000
      }

      return 0
    }

    return [...matchdays].sort((a, b) => {
      const orderA = getSortOrder(a.matchDay)
      const orderB = getSortOrder(b.matchDay)

      if (orderA < 0 && orderB < 0) {
        return Math.abs(orderA) - Math.abs(orderB)
      }

      return orderA - orderB
    })
  }

  const handleClosureRateChange = (name, value) => {
    setClosureRates((prevRates) => ({
      ...prevRates,
      [name]: value,
    }))
  }

  const handleHighlightRateChange = (name, value) => {
    setHighlightRates((prevRates) => ({
      ...prevRates,
      [name]: value,
    }))
  }

  const handleClosureTimeChange = (name, value) => {
    setClosureTimes((prevRates) => ({
      ...prevRates,
      [name]: value,
    }))
  }

  const handleHighlightTimeChange = (name, value) => {
    setHighlightTimes((prevRates) => ({
      ...prevRates,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    // Guarded by this page's own save state, not the app-wide isLoading flag,
    // which any unrelated request can hold and silently swallow the save.
    if (isSaving || !matchDay) return

    setIsSaving(true)
    setError('')
    try {
      const response = await updatematchdaydata(
        matchDay,
        competitionId,
        twitterNotified,
        twitterSuspended,
        tiktokNotified,
        tiktokSuspended,
        telegramNotified,
        telegramSuspended,
        telegramNonSuspended,
        discordSuspended,
        snapchatNotified,
        telegramImpacted,
        closureRates,
        highlightRates,
        closureTimes,
        highlightTimes,
      )
      if (response === 'success') {
        fetchMatchDaysData(competitionId)
        setIsOpen(false)
        navigate('/edit-matchday-data')
      }
    } catch (error) {
      setError('Error: ' + error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <AppLayout pageName='Matchday Data'>
      <section>
        {matchDay ? (
          <div className='text-2xl font-bold text-main mb-2'>Update Matchday {matchDay} Data</div>
        ) : (
          <div className='flex flex-wrap flex-col lg:flex-row justify-between'>
            <div className='inline-flex rounded-md mb-3'>
              <CompetitionSelector
                value={slctdCompetition}
                competitions={competitions}
                onChange={(v) => setslctdCompetition(v)}
              />
            </div>
          </div>
        )}

        {isOpen && (
          <section className='w-full !mb-4'>
            <form
              onSubmit={handleSubmit}
              className=''
              method='post'
            >
              <div className='grid grid-cols-4 gap-2'>
                <div className='p-2'>
                  <label htmlFor='twitterNotified'>
                    Accounts Notified - <b className='text-main-alt'>Twitter</b>
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none'>
                      <img
                        alt='twitter'
                        src='/images/icons/x.png'
                        className='w-6 h-6'
                      />
                    </div>
                    <input
                      id='twitterNotified'
                      name='twitterNotified'
                      type='number'
                      className='form-control !pl-12'
                      min={0}
                      step={1}
                      required
                      value={twitterNotified}
                      onChange={(e) => setTwitterNotified(e.target.value)}
                    />
                  </div>
                </div>
                <div className='p-2'>
                  <label htmlFor='twitterSuspended'>
                    Accounts Suspended - <b className='text-main-alt'>Twitter</b>
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none'>
                      <img
                        alt='twitter'
                        src='/images/icons/x.png'
                        className='w-6 h-6'
                      />
                    </div>
                    <input
                      id='twitterSuspended'
                      name='twitterSuspended'
                      type='number'
                      className='form-control !pl-12'
                      min={0}
                      step={1}
                      required
                      value={twitterSuspended}
                      onChange={(e) => setTwitterSuspended(e.target.value)}
                    />
                  </div>
                </div>

                <div className='p-2'>
                  <label htmlFor='tiktokNotified'>
                    Accounts Notified - <b className='text-main-alt'>Tiktok</b>
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none'>
                      <img
                        alt='twitter'
                        src='/images/icons/tiktok.png'
                        className='w-6 h-6'
                      />
                    </div>
                    <input
                      id='tiktokNotified'
                      name='tiktokNotified'
                      type='number'
                      className='form-control !pl-12'
                      min={0}
                      step={1}
                      required
                      value={tiktokNotified}
                      onChange={(e) => setTiktokNotified(e.target.value)}
                    />
                  </div>
                </div>
                <div className='p-2'>
                  <label htmlFor='tiktokSuspended'>
                    Accounts Suspended - <b className='text-main-alt'>Tiktok</b>
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none'>
                      <img
                        alt='twitter'
                        src='/images/icons/tiktok.png'
                        className='w-6 h-6'
                      />
                    </div>
                    <input
                      id='tiktokSuspended'
                      name='tiktokSuspended'
                      type='number'
                      className='form-control !pl-12'
                      min={0}
                      step={1}
                      required
                      value={tiktokSuspended}
                      onChange={(e) => setTiktokSuspended(e.target.value)}
                    />
                  </div>
                </div>

                <div className='p-2'>
                  <label htmlFor='telegramNotified'>
                    Channels Notified - <b className='text-main-alt'>Telegram</b>
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none'>
                      <img
                        alt='twitter'
                        src='/images/icons/telegram.png'
                        className='w-6 h-6'
                      />
                    </div>
                    <input
                      id='telegramNotified'
                      name='telegramNotified'
                      type='number'
                      className='form-control !pl-12'
                      min={0}
                      step={1}
                      required
                      value={telegramNotified}
                      onChange={(e) => setTelegramNotified(e.target.value)}
                    />
                  </div>
                </div>
                <div className='p-2'>
                  <label htmlFor='telegramSuspended'>
                    Channels Suspended - <b className='text-main-alt'>Telegram</b>
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none'>
                      <img
                        alt='twitter'
                        src='/images/icons/telegram.png'
                        className='w-6 h-6'
                      />
                    </div>
                    <input
                      id='telegramSuspended'
                      name='telegramSuspended'
                      type='number'
                      className='form-control !pl-12'
                      min={0}
                      step={1}
                      required
                      value={telegramSuspended}
                      onChange={(e) => setTelegramSuspended(e.target.value)}
                    />
                  </div>
                </div>
                <div className='p-2'>
                  <label htmlFor='telegramNonSuspended'>
                    Channels Non Suspended - <b className='text-main-alt'>Telegram</b>
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none'>
                      <img
                        alt='twitter'
                        src='/images/icons/telegram.png'
                        className='w-6 h-6'
                      />
                    </div>
                    <input
                      id='telegramNonSuspended'
                      name='telegramNonSuspended'
                      type='number'
                      className='form-control !pl-12'
                      min={0}
                      step={1}
                      required
                      value={telegramNonSuspended}
                      onChange={(e) => setTelegramNonSuspended(e.target.value)}
                    />
                  </div>
                </div>
                <div className='p-2'>
                  <label htmlFor='snapchatNotified'>
                    Accounts Notified - <b className='text-main-alt'>Snapchat</b>
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none'>
                      <img
                        alt='twitter'
                        src='/images/icons/snapchat.png'
                        className='w-6 h-6'
                      />
                    </div>
                    <input
                      id='snapchatNotified'
                      name='snapchatNotified'
                      type='number'
                      className='form-control !pl-12'
                      min={0}
                      step={1}
                      required
                      value={snapchatNotified}
                      onChange={(e) => setSnapchatNotified(e.target.value)}
                    />
                  </div>
                </div>
                <div className='p-2'>
                  <label htmlFor='discordSuspended'>
                    Accounts Suspended - <b className='text-main-alt'>Discord</b>
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none'>
                      <img
                        alt='twitter'
                        src='/images/icons/discord.png'
                        className='w-6 h-6'
                      />
                    </div>
                    <input
                      id='discordSuspended'
                      name='discordSuspended'
                      type='number'
                      className='form-control !pl-12'
                      min={0}
                      step={1}
                      required
                      value={discordSuspended}
                      onChange={(e) => setDiscordSuspended(e.target.value)}
                    />
                  </div>
                </div>
                <div className='p-2'>
                  <label htmlFor='telegramImpacted'>
                    Impacted Members - <b className='text-main-alt'>Telegram</b>
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 start-0 flex items-center ps-3.5 pointer-events-none'>
                      <img
                        alt='twitter'
                        src='/images/icons/telegram.png'
                        className='w-6 h-6'
                      />
                    </div>
                    <input
                      id='telegramImpacted'
                      name='telegramImpacted'
                      type='number'
                      className='form-control !pl-12'
                      min={0}
                      step={1}
                      required
                      value={telegramImpacted}
                      onChange={(e) => setTelegramImpacted(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className='px-2 pb-2'>
                {error !== '' ? (
                  <div className='text-left text-red-500 font-semibold text-md mb-2 tracking-wide'>{error}</div>
                ) : (
                  <div className='py-4'></div>
                )}
                <div className='flex space-x-3'>
                  <button
                    type='submit'
                    className='btn-submit'
                    disabled={isSaving}
                  >
                    <span>{matchDay ? 'Save Changes' : 'Submit'}</span>
                    <span>
                      {!isSaving ? (
                        <HiArrowNarrowRight />
                      ) : (
                        <Spinner
                          size={14}
                          className='ml-2'
                          animating={isSaving}
                        />
                      )}
                    </span>
                  </button>
                  {matchDay && (
                    <Link
                      to={'/edit-matchday-data'}
                      className='btn-cancel'
                    >
                      Cancel
                    </Link>
                  )}
                </div>
              </div>
              
              <div className='px-2 py-5'>
                <hr />
              </div>

              <div className='px-2'>
                <table className='w-full text-sm text-center text-gray-800'>
                  <thead className='text-xs text-neutral-800 uppercase bg-main-alt'>
                    <tr>
                      <th
                        scope='col'
                        className='px-6 py-4 text-left'
                      >
                        Platforms
                      </th>
                      <th
                        scope='col'
                        className='px-6 py-4'
                      >
                        Closure Rate - Live
                      </th>
                      <th
                        scope='col'
                        className='px-6 py-4'
                      >
                        Closure Rate - Highlight
                      </th>
                      <th
                        scope='col'
                        className='px-6 py-4'
                      >
                        Average Closure Time - Live
                      </th>
                      <th
                        scope='col'
                        className='px-6 py-4'
                      >
                        Average Closure Time - Highlight
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {closureData?.map((website, index) => {
                      return (
                        <tr
                          key={index}
                          className='bg-white border-b-2 border-blue-100'
                        >
                          <th
                            scope='row'
                            className='px-6 py-4 font-medium border-r-2 border-blue-100 whitespace-nowrap text-left'
                          >
                            <div className='flex space-x-2 items-center'>
                              <img
                                width='20'
                                height='20'
                                src={`${process.env.REACT_APP_STATIC_URL || ''}${website.icon}`}
                              />
                              <div>
                                <h6 className='text-neutral-900 text-lg'>{website.name}</h6>
                              </div>
                            </div>
                          </th>
                          <td className='px-6 py-2 text-lg'>
                            <input
                              type='number'
                              className='form-control !py-1'
                              min={0}
                              max={100}
                              step={1}
                              value={closureRates[website.id] ?? ''}
                              onChange={(e) => handleClosureRateChange(website.id, e.target.value)}
                            />
                          </td>
                          <td className='px-6 py-2 text-lg'>
                            <input
                              type='number'
                              className='form-control !py-1'
                              min={0}
                              max={100}
                              step={1}
                              value={highlightRates[website.id] ?? ''}
                              onChange={(e) => handleHighlightRateChange(website.id, e.target.value)}
                            />
                          </td>
                          <td className='px-6 py-2 text-lg'>
                            <input
                              className='form-control !py-1'
                              maxLength={30}
                              value={closureTimes[website.id] ?? ''}
                              onChange={(e) => handleClosureTimeChange(website.id, e.target.value)}
                            />
                          </td>
                          <td className='px-6 py-2 text-lg'>
                            <input
                              className='form-control !py-1'
                              maxLength={30}
                              value={highlightTimes[website.id] ?? ''}
                              onChange={(e) => handleHighlightTimeChange(website.id, e.target.value)}
                            />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className='px-2 pb-2'>
                {error !== '' ? (
                  <div className='text-left text-red-500 font-semibold text-md mb-2 tracking-wide'>{error}</div>
                ) : (
                  <div className='py-4'></div>
                )}
                <div className='flex space-x-3'>
                  <button
                    type='submit'
                    className='btn-submit'
                    disabled={isSaving}
                  >
                    <span>{matchDay ? 'Save Changes' : 'Submit'}</span>
                    <span>
                      {!isSaving ? (
                        <HiArrowNarrowRight />
                      ) : (
                        <Spinner
                          size={14}
                          className='ml-2'
                          animating={isSaving}
                        />
                      )}
                    </span>
                  </button>
                  {matchDay && (
                    <Link
                      to={'/edit-matchday-data'}
                      className='btn-cancel'
                    >
                      Cancel
                    </Link>
                  )}
                </div>
              </div>
            </form>
          </section>
        )}

        {isLoading ? (
          <div className='flex justify-center w-full py-12'>
            <Spinner
              color='#CCCCCC'
              size={24}
            />
          </div>
        ) : (
          <>
            <div className='matchday-table'>
              <table className='table text-center mt-3'>
                <thead>
                  <tr>
                    <th></th>
                    <th colSpan={2}>Twitter</th>
                    <th colSpan={2}>Tiktok</th>
                    <th colSpan={3}>Telegram</th>
                    <th>Discord</th>
                    <th>Snapchat</th>
                    <th></th>
                  </tr>
                  <tr>
                    <th
                      rowSpan={2}
                      scope='col'
                    >
                      MatchDay
                    </th>
                    <th>Accounts Notified</th>
                    <th>Accounts Suspended</th>
                    <th>Accounts Notified</th>
                    <th>Accounts Suspended</th>
                    <th>Channels Notified</th>
                    <th>Channels Suspended</th>
                    <th>Impacted Members</th>
                    <th>Accounts Suspended</th>
                    <th>Accounts Notified</th>
                    <th
                      rowSpan={2}
                      scope='col'
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortMatchdays(matchdays)?.map((matchday, index) => {
                    return (
                      <tr
                        key={index}
                        className='border-t font-medium'
                      >
                        <td>{matchday.matchDay}</td>
                        <td>{matchday.accnotifiedtwitter}</td>
                        <td>{matchday.accsuspendedtwitter}</td>
                        <td>{matchday.accnotifiedtiktok}</td>
                        <td>{matchday.accsuspendedtiktok}</td>
                        <td>{matchday.accnotifiedtelegram}</td>
                        <td>{matchday.accsuspendedtelegram}</td>
                        <td>{matchday.membersinspactedtelegram}</td>
                        <td>{matchday.accsuspendeddiscord}</td>
                        <td>{matchday.accnotifiedsnapchat}</td>
                        <td>
                          {/* Matchdays without a name can't be addressed by the edit route */}
                          {String(matchday.matchDay ?? '').trim() !== '' && (
                            <Link
                              className='text-edit'
                              to={`/edit-matchday-data/${matchdaysCompetitionId}/${encodeURIComponent(matchday.matchDay)}`}
                            >
                              Edit
                            </Link>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </AppLayout>
  )
}

export default EditMatchdayData
